import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import DynamicTable from "../../../data/DynamicTable/dynamicTable";
import { companyManagementUrl, salesManagement } from "../../../config";
import { useEffect, useState } from "react";
import { PulseLoader } from "react-spinners";
import { AllSaleOrders, ReversedSaleorders } from "../../../data/Axios/queries";
import { graphqlQuery } from "../../../data/Axios/DynamicService";
import { tokens } from "../../../theme";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PeopleIcon from "@mui/icons-material/People";
import PaymentIcon from "@mui/icons-material/Payment";
import { format } from "date-fns";

const base_url = salesManagement.uri;
const REVERSED_SALE_ORDERS_PAGINATED_QUERY = `
  query ReversedSaleOrdersPaginated($pageNumber: Int, $pageSize: Int) {
    reversedSaleOrdersPaginatedFunc(functionparams: { 
      pageNumber: $pageNumber, 
      pageSize: $pageSize 
    }) {
      branchId
      branchName
      cancelled
      companyId
      companyName
      createdBy
      createdDate
      currentPage
      customerBalance
      customerId
      customerName
      customerTypeId
      firstRecordInPage
      int
      isCredit
      isInvoiced
      isPaid
      lastRecordInPage
      modifiedBy
      outstanding
      pageSize
      paymentMethod
      paymentMethodId
      phoneNumber
      regionId
      reversed
      saleDate
      saleOrderId
      salesAgent
      totalAmount
      totalPages
      totalRecords
      typeName
      updatedBy
      updatedDate
      paidDate
    }
  }
`;

const actions = {
  entity: "SalesOrder",
  isSubRight: true,
  ViewReversSaleOrderItems: {
    key: "MARK_TASK_DONE",
    button_name: "View Order Items",
    Show_Button: true,
    permission: "hasNoPermission",
    roleGroupId: [1, 2, 3, 4],
  },

  showMore: {
    key: "SHOWMORE",
    button_name: "Show More",
    Show_Button: true,
    permission: "hasNoPermission",
    roleGroupId: [1, 2, 3, 4],
    title: "Sales Order Details",
  },
  showStatus: true,
  showCancelled: false,
};

function ReversedSaleorder() {
  const [saleOrders, setSaleOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [creditStatusFilter, setCreditStatusFilter] = useState("all");
  const [invoiceStatusFilter, setInvoiceStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [cancelledFilter, setCancelledFilter] = useState("all");
  const [branchFilter, setBranchFilter] = useState("all");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("all");

  // Dropdown options
  const [branches, setBranches] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);

  // Statistics
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalSales: 0,
    paidOrders: 0,
    unpaidOrders: 0,
    creditSales: 0,
    cashSales: 0,
    totalOutstanding: 0,
    invoicedOrders: 0,
    uninvoicedOrders: 0,
    cancelledOrders: 0,
    activeOrders: 0,
    uniqueCustomers: 0,
    averageSaleValue: 0,
    averageOutstanding: 0,
  });

  // MUI theme-aware colors
  const muiTheme = useTheme();
  const colors = tokens(muiTheme.palette.mode);
  const isDark = muiTheme.palette.mode === "dark";

  const theme = {
    primary: isDark ? colors.blueAccent[400] : "#1976D2",
    secondary: isDark ? colors.blueAccent[500] : "#0288D1",
    success: "#4CAF50",
    warning: "#FF9800",
    error: "#F44336",
    info: isDark ? colors.blueAccent[300] : "#2196F3",
    purple: "#9C27B0",
    lightBg: isDark ? alpha(colors.blueAccent[400], 0.15) : "#E3F2FD",
    cardBg: isDark ? colors.primary[400] : "#FFFFFF",
    pageBg: isDark ? colors.primary[500] : "#F5F7FA",
  };

  const ignore = [
    "isActive",
    "companyId",
    "cancelled",
    "customerBalance",
    "customerId", "createdBy", "saleOrderId", "updatedBy", "paymentMethodId", "CreatedBy", "UpdatedBy", "saleOrderId",
    "createdDate",
    "addedBy",
    "modifiedBy",
    "cancelled",
    "phoneNumber",
    "updatedDate",
    "reason"
  ];
  const ignoreShowMore = ["isActive", "companyId", "cancelled", "customerBalance", "customerId",
    "createdBy",
    "saleOrderId",
    "updatedBy",
    "paymentMethodId",
    "CreatedBy",
    "UpdatedBy",
    "branchId",
  ];

  const unignoredIds = ["saleOrderId", "isPaid"];

  // ✅ Calculate statistics
  const calculateStats = (data) => {
    const totalOrders = data.length;
    const totalSales = data.reduce((sum, order) => sum + (order.netAmount || 0), 0);
    const totalOutstanding = data.reduce(
      (sum, order) => sum + (order.outstanding || 0),
      0
    );

    const paidOrders = data.filter((order) => order.isPaid === true).length;
    const unpaidOrders = data.filter((order) => order.isPaid === false).length;

    const creditSales = data.filter((order) => order.isCredit === true).length;
    const cashSales = data.filter((order) => order.isCredit === false).length;

    const invoicedOrders = data.filter((order) => order.isInvoiced === true).length;
    const uninvoicedOrders = data.filter((order) => order.isInvoiced === false).length;

    const cancelledOrders = data.filter((order) => order.cancelled === true).length;
    const activeOrders = data.filter((order) => order.cancelled === false).length;

    // Count unique customers
    const uniqueCustomers = new Set(
      data.map((order) => order.customerId).filter((id) => id != null)
    ).size;

    // Average sale value
    const averageSaleValue = totalOrders > 0 ? totalSales / totalOrders : 0;

    // Average outstanding balance (only for orders with outstanding > 0)
    const ordersWithOutstanding = data.filter((order) => (order.outstanding || 0) > 0);
    const averageOutstanding =
      ordersWithOutstanding.length > 0
        ? totalOutstanding / ordersWithOutstanding.length
        : 0;

    setStats({
      totalOrders,
      totalSales,
      paidOrders,
      unpaidOrders,
      creditSales,
      cashSales,
      totalOutstanding,
      invoicedOrders,
      uninvoicedOrders,
      cancelledOrders,
      activeOrders,
      uniqueCustomers,
      averageSaleValue,
      averageOutstanding,
    });
  };

  // ✅ Extract unique branches and payment methods
  const extractFilterOptions = (data) => {
    const uniqueBranches = [
      ...new Set(
        data
          .map((order) => order.branchName)
          .filter((branch) => branch != null && branch !== "")
      ),
    ].sort();

    const uniquePaymentMethods = [
      ...new Set(
        data
          .map((order) => order.paymentMethod)
          .filter((method) => method != null && method !== "")
      ),
    ].sort();

    setBranches(uniqueBranches);
    setPaymentMethods(uniquePaymentMethods);
  };

  // ✅ Fetch sale orders
  const fetchSaleOrders = async () => {
    try {
      setLoading(true);
      setError("");
      const variables = {
        pageNumber: page + 1, // Adjust for 1-based indexing
        pageSize: pageSize,
      };
      const response = await graphqlQuery(REVERSED_SALE_ORDERS_PAGINATED_QUERY, base_url, variables); 
      // const response = await graphqlQuery(ReversedSaleorders, base_url);

      if (Array.isArray(response)) {
        const ordersData = response.map((order) => ({
          ...order,
          formattedSaleDate: order.saleDate
            ? format(new Date(order.saleDate), "yyyy-MM-dd")
            : "",
          formattedCreatedDate: order.createdDate
            ? format(new Date(order.createdDate), "yyyy-MM-dd")
            : "",
        }));

        setSaleOrders(ordersData);
        extractFilterOptions(ordersData);
        applyFilters(
          ordersData,
          searchQuery,
          paymentStatusFilter,
          creditStatusFilter,
          invoiceStatusFilter,
          dateFrom,
          dateTo,
          cancelledFilter,
          branchFilter,
          paymentMethodFilter
        );
      }
    } catch (error) {
      console.error("Error fetching sale orders:", error);
      setError("Failed to load sale orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Apply all filters
  const applyFilters = (
    data,
    search,
    payment,
    credit,
    invoice,
    fromDate,
    toDate,
    cancelled,
    branch,
    paymentMethod
  ) => {
    let filtered = [...data];

    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter((order) => {
        const fields = [
          order.customerName,
          order.phoneNumber,
          order.saleOrderId,
          order.companyName,
          order.branchName,
          order.addedBy,
          order.modifiedBy,
        ];

        return fields.some((field) => {
          if (field == null) return false;
          return String(field).toLowerCase().includes(searchLower);
        });
      });
    }

    // Payment status filter
    if (payment !== "all") {
      const paymentValue = payment === "true";
      filtered = filtered.filter((order) => order.isPaid === paymentValue);
    }

    // Credit status filter
    if (credit !== "all") {
      const creditValue = credit === "true";
      filtered = filtered.filter((order) => order.isCredit === creditValue);
    }

    // Invoice status filter
    if (invoice !== "all") {
      const invoiceValue = invoice === "true";
      filtered = filtered.filter((order) => order.isInvoiced === invoiceValue);
    }

    // Cancelled filter
    if (cancelled !== "all") {
      const cancelledValue = cancelled === "true";
      filtered = filtered.filter((order) => order.cancelled === cancelledValue);
    }

    // Branch filter
    if (branch !== "all") {
      filtered = filtered.filter((order) => order.branchName === branch);
    }

    // Payment method filter
    if (paymentMethod !== "all") {
      filtered = filtered.filter((order) => order.paymentMethod === paymentMethod);
    }

    // Date range filter
    if (fromDate) {
      const fromDateObj = new Date(fromDate);
      fromDateObj.setHours(0, 0, 0, 0);
      filtered = filtered.filter((order) => {
        const orderDate = order.saleDate || order.createdDate;
        if (!orderDate) return false;
        const rowDate = new Date(orderDate);
        rowDate.setHours(0, 0, 0, 0);
        return rowDate >= fromDateObj;
      });
    }

    if (toDate) {
      const toDateObj = new Date(toDate);
      toDateObj.setHours(23, 59, 59, 999);
      filtered = filtered.filter((order) => {
        const orderDate = order.saleDate || order.createdDate;
        if (!orderDate) return false;
        const rowDate = new Date(orderDate);
        rowDate.setHours(0, 0, 0, 0);
        return rowDate <= toDateObj;
      });
    }

    setFilteredOrders(filtered);
    calculateStats(filtered);

    // Calculate total amount
    const total = filtered.reduce((sum, order) => sum + (order.totalAmount || 0), 0);
    const formattedTotal = total.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    setTotalAmount(formattedTotal);
  };

  // ✅ Handle filter changes
  useEffect(() => {
    if (saleOrders.length > 0) {
      applyFilters(
        saleOrders,
        searchQuery,
        paymentStatusFilter,
        creditStatusFilter,
        invoiceStatusFilter,
        dateFrom,
        dateTo,
        cancelledFilter,
        branchFilter,
        paymentMethodFilter
      );
    }
  }, [
    searchQuery,
    paymentStatusFilter,
    creditStatusFilter,
    invoiceStatusFilter,
    dateFrom,
    dateTo,
    cancelledFilter,
    branchFilter,
    paymentMethodFilter,
  ]);

  // ✅ Handle refresh
  const handleRefresh = () => {
    fetchSaleOrders();
  };

  // ✅ Clear all filters
  const handleClearFilters = () => {
    setSearchQuery("");
    setPaymentStatusFilter("all");
    setCreditStatusFilter("all");
    setInvoiceStatusFilter("all");
    setDateFrom("");
    setDateTo("");
    setCancelledFilter("all");
    setBranchFilter("all");
    setPaymentMethodFilter("all");
  };

  // ✅ Check if filters are active
  const hasActiveFilters =
    searchQuery ||
    paymentStatusFilter !== "all" ||
    creditStatusFilter !== "all" ||
    invoiceStatusFilter !== "all" ||
    dateFrom ||
    dateTo ||
    cancelledFilter !== "all" ||
    branchFilter !== "all" ||
    paymentMethodFilter !== "all";

  // ✅ Fetch data on component mount
  useEffect(() => {
    fetchSaleOrders();
  }, []);
  const handlePageChange = (newPage) => setPage(newPage);
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  return (
    <Box sx={{ p: 3, bgcolor: theme.pageBg, minHeight: "100vh" }}>
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <ShoppingBagIcon sx={{ fontSize: 32, color: theme.primary, mr: 1 }} />
          <Typography variant="h4" fontWeight="700" color={theme.primary}>
            Reversed Sales Order Management
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Track and manage all reversed sales orders and customer transactions
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Filter and Actions Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 2,
          bgcolor: theme.cardBg,
        }}
      >
        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <FilterListIcon sx={{ color: theme.primary }} />
          <Typography variant="h6" fontWeight="600">
            Filters
          </Typography>
          {hasActiveFilters && (
            <Chip
              label={`${[
                searchQuery,
                paymentStatusFilter !== "all",
                creditStatusFilter !== "all",
                invoiceStatusFilter !== "all",
                dateFrom,
                dateTo,
                cancelledFilter !== "all",
                branchFilter !== "all",
                paymentMethodFilter !== "all",
              ].filter(Boolean).length
                } Active`}
              size="small"
              color="primary"
              onDelete={handleClearFilters}
            />
          )}
        </Box>

        <Grid container spacing={2} alignItems="center">
          {/* Search */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search customer, phone, order ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Branch Filter */}
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Branch</InputLabel>
              <Select
                value={branchFilter}
                label="Branch"
                onChange={(e) => setBranchFilter(e.target.value)}
              >
                <MenuItem value="all">All Branches</MenuItem>
                {branches.map((branch) => (
                  <MenuItem key={branch} value={branch}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <StorefrontIcon sx={{ fontSize: 18, color: theme.primary }} />
                      {branch}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Payment Status Filter */}
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment</InputLabel>
              <Select
                value={paymentStatusFilter}
                label="Payment"
                onChange={(e) => setPaymentStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: theme.success }} />
                    Paid
                  </Box>
                </MenuItem>
                <MenuItem value="false">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PendingActionsIcon sx={{ fontSize: 18, color: theme.warning }} />
                    Unpaid
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Credit/Cash Filter */}
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Sale Type</InputLabel>
              <Select
                value={creditStatusFilter}
                label="Sale Type"
                onChange={(e) => setCreditStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CreditCardIcon sx={{ fontSize: 18, color: theme.info }} />
                    Credit
                  </Box>
                </MenuItem>
                <MenuItem value="false">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AttachMoneyIcon sx={{ fontSize: 18, color: theme.success }} />
                    Cash
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Invoice Status Filter */}
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Invoice</InputLabel>
              <Select
                value={invoiceStatusFilter}
                label="Invoice"
                onChange={(e) => setInvoiceStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="true">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 18, color: theme.success }} />
                    Invoiced
                  </Box>
                </MenuItem>
                <MenuItem value="false">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PendingActionsIcon sx={{ fontSize: 18, color: theme.warning }} />
                    Uninvoiced
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Payment Method Filter */}
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Method</InputLabel>
              <Select
                value={paymentMethodFilter}
                label="Payment Method"
                onChange={(e) => setPaymentMethodFilter(e.target.value)}
              >
                <MenuItem value="all">All Methods</MenuItem>
                {paymentMethods.map((method) => (
                  <MenuItem key={method} value={method}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PaymentIcon sx={{ fontSize: 18, color: theme.secondary }} />
                      {method}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Cancelled Filter */}
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={cancelledFilter}
                label="Status"
                onChange={(e) => setCancelledFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="false">Active</MenuItem>
                <MenuItem value="true">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          {/* From Date */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </Grid>

          {/* To Date */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </Grid>

          {/* Actions */}
          <Grid item xs={12} md={0.5}>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{
                    bgcolor: theme.lightBg,
                    "&:hover": { bgcolor: theme.primary, color: "white" },
                  }}
                >
                  {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
            {searchQuery && (
              <Chip
                label={`Search: "${searchQuery}"`}
                size="small"
                onDelete={() => setSearchQuery("")}
                color="primary"
                variant="outlined"
              />
            )}
            {branchFilter !== "all" && (
              <Chip
                label={`Branch: ${branchFilter}`}
                size="small"
                onDelete={() => setBranchFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
            {paymentStatusFilter !== "all" && (
              <Chip
                label={`Payment: ${paymentStatusFilter === "true" ? "Paid" : "Unpaid"}`}
                size="small"
                onDelete={() => setPaymentStatusFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
            {creditStatusFilter !== "all" && (
              <Chip
                label={`Type: ${creditStatusFilter === "true" ? "Credit" : "Cash"}`}
                size="small"
                onDelete={() => setCreditStatusFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
            {invoiceStatusFilter !== "all" && (
              <Chip
                label={`Invoice: ${invoiceStatusFilter === "true" ? "Invoiced" : "Uninvoiced"}`}
                size="small"
                onDelete={() => setInvoiceStatusFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
            {paymentMethodFilter !== "all" && (
              <Chip
                label={`Method: ${paymentMethodFilter}`}
                size="small"
                onDelete={() => setPaymentMethodFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
            {dateFrom && (
              <Chip
                label={`From: ${dateFrom}`}
                size="small"
                onDelete={() => setDateFrom("")}
                color="primary"
                variant="outlined"
              />
            )}
            {dateTo && (
              <Chip
                label={`To: ${dateTo}`}
                size="small"
                onDelete={() => setDateTo("")}
                color="primary"
                variant="outlined"
              />
            )}
            {cancelledFilter !== "all" && (
              <Chip
                label={`Status: ${cancelledFilter === "false" ? "Active" : "Cancelled"}`}
                size="small"
                onDelete={() => setCancelledFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        )}
      </Paper>

      {/* Dynamic Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: theme.cardBg,
        }}
      >
        {loading && filteredOrders.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 400,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DynamicTable
            title="Reversed Sales Orders"
            subtitle={`Showing ${filteredOrders.length} orders ${hasActiveFilters ? "(filtered)" : ""
              }`}
            ignoreFields={ignore}
            ignoreShowMore={ignoreShowMore}
            // FormComponent={SalesOrderForm}
            columns={[
              { field: "saleOrderId", headerName: "Order ID", width: 50 },
              { field: "customerName", headerName: "Customer", width: 100 },
              { field: "paymentMethod", headerName: "Payment Method", width: 100 },
              { field: "totalAmount", headerName: "Total Amount", width: 100 },
              { field: "outstanding", headerName: "Outstanding Amount", width: 100 },
              { field: "isPaid", headerName: "Is Paid", width: 100 },
              { field: "saleDate", headerName: "Sale Date", width: 100 },
              { field: "salesAgent", headerName: "Sale Agent", width: 100 },
              { field: "branchName", headerName: "Branch Name", width: 100 },
              { field: "reversed", headerName: "Is Reversed", width: 50 },
            ]}
            rows={saleOrders}
            rowCount={rowCount}
            page={page}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            paginationMode="server"
            pagination={true}
            query={REVERSED_SALE_ORDERS_PAGINATED_QUERY}
            base_url={base_url}
            actions={actions}
            unignoredIds={unignoredIds}
            filteredData={filteredOrders}
            isfilteredData={true}
            getRowId={(row) => row.saleOrderId}
          />
        )}
      </Paper>
    </Box>
  );
}

export default ReversedSaleorder;